import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { MoonLoader } from "react-spinners";
import UserContext from "../../contexts/UserContext";
import NotificationsContext from "../../contexts/NotificationsContext";
import Swal from "sweetalert2";
import MarketplaceSlugServices from "./services/MarketplaceSlugServices";
import MarketplaceServices from "./services/MarketplaceServices";
import "./SellerProfile.modern.css";

const SellerProfile = () => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { setNotify } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [error, setError] = useState(null);

  // Toast personalizado para notificações
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    timer: 4000,
    timerProgressBar: true,
    showConfirmButton: false,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [checkingName, setCheckingName] = useState(false);
  const [nameAvailability, setNameAvailability] = useState({
    checked: false,
    available: null,
    error: null,
    suggestions: [],
    normalizedSlug: null
  });
  

  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    formState: { errors },
  } = useForm();


  // Buscar dados do perfil
  useEffect(() => {
    loadProfile();
  }, []);

  // Função para verificar disponibilidade do nome
  const handleCheckNameAvailability = async () => {
    const displayName = watch("display_name");
    
    if (!displayName || displayName.trim().length < 2) {
      setNameAvailability({
        checked: true,
        available: false,
        error: "Nome deve ter pelo menos 2 caracteres",
        suggestions: [],
        normalizedSlug: null
      });
      return;
    }

    setCheckingName(true);
    try {
      const result = await MarketplaceSlugServices.checkDisplayNameAvailability(displayName.trim(), 'profile');
      
      setNameAvailability({
        checked: true,
        available: result.available,
        error: result.error || null,
        suggestions: result.suggestions || [],
        normalizedSlug: result.normalized_slug || result.slug
      });

      if (!result.success && result.error) {
        console.error('Erro na verificação:', result.error);
      }
    } catch (error) {
      console.error('Erro ao verificar disponibilidade:', error);
      setNameAvailability({
        checked: true,
        available: false,
        error: "Erro ao verificar disponibilidade. Tente novamente.",
        suggestions: [],
        normalizedSlug: null
      });
    } finally {
      setCheckingName(false);
    }
  };


  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await MarketplaceServices.loadSellerProfile();
      
      // Nova estrutura da API: response.data contém has_profile, profile, user, store, stats
      if (response.data?.has_profile && response.data?.profile) {
        const profileData = response.data.profile;
        const userData = response.data.user;
        const storeData = response.data.store;
        const statsData = response.data.stats;
        
        // Salvar todos os dados recebidos
        setProfileData({
          ...profileData,
          user: userData,
          store: storeData,
          stats: statsData,
          has_store: response.data.has_store,
          can_create_store: response.data.can_create_store
        });
        
        // Popular os campos do formulário
        setValue("display_name", profileData.display_name || "");
        setValue("bio", profileData.bio || "");
        setValue("public_email", profileData.public_email || "");
        setValue("website", profileData.website ? profileData.website.replace(/^https?:\/\//, '') : "");
        
        // Se existe um nome no perfil, marca como verificado
        if (profileData.display_name) {
          setNameAvailability({
            checked: true,
            available: true, // Se já existe no perfil, é considerado válido
            error: null,
            suggestions: [],
            normalizedSlug: profileData.slug || MarketplaceSlugServices.generateSlug(profileData.display_name)
          });
        }
      } else if (response.data && !response.data.has_profile) {
        // API retornou dados mas não há perfil - criar um novo
        setProfileData(null);
      }
    } catch (error) {
      console.error("Erro ao carregar perfil do vendedor:", error);
      if (error.response?.status === 404) {
        // Perfil não existe ainda, criar um novo
        setProfileData(null);
      } else {
        setError("Erro ao carregar dados do perfil. Tente novamente.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Função para preview da imagem
  const handleImagePreview = (image) => {
    if (!image) return;

    if (image.size > 4000000) {
      // Usar Toast para alertas de validação
      Toast.fire({
        icon: "warning",
        title: "Imagem muito grande!",
        text: "Por favor, use imagens até 4MB.",
      });

      // Usar setNotify para informação adicional
      setNotify({
        title: "Arquivo rejeitado",
        message: "A imagem selecionada excede o limite de 4MB. Comprima ou escolha outra imagem.",
        type: "warning",
        visible: true,
      });
      return;
    }

    setSelectedFile(image);
    const url = URL.createObjectURL(image);
    setImageFile(url);
  
  };

  // Função para obter URL da imagem
  const getProfileImage = () => {
    if (imageFile) {
      return imageFile;
    } else if (profileData?.avatar) {
      return `https://mercadoagentes.s3.amazonaws.com/${profileData.avatar}`; // hardcoded por enquanto
    }
    return null;
  };


  const onSubmit = async (formData) => {
    // Verificar se o nome foi validado
    if (!nameAvailability.checked || nameAvailability.available === false) {
      // Usar Swal.fire para warning que bloqueia ação
      await Swal.fire({
        icon: "warning",
        title: "Nome não verificado",
        text: "Por favor, verifique a disponibilidade do nome de exibição antes de salvar.",
        confirmButtonColor: "var(--primary)",
      });

      // Usar setNotify para informação adicional
      setNotify({
        title: "Verificação necessária",
        message: "Clique em 'Verificar' ao lado do campo Nome de Exibição.",
        type: "info",
        visible: true,
      });
      return;
    }

    setSaving(true);
    try {
      // Gerar slug a partir do nome verificado
      const slug = MarketplaceSlugServices.generateSlug(formData.display_name);
      
      // Verificar se há arquivo para upload
      const hasFile = selectedFile && selectedFile instanceof File;
      
      let response;
      
      let submitData;
      
      if (hasFile) {
        // Usar FormData para envio com arquivo
        submitData = new FormData();
        submitData.append("display_name", formData.display_name);
        submitData.append("bio", formData.bio);
        submitData.append("public_email", formData.public_email);
        submitData.append("website", formData.website ? `https://${formData.website}` : "");
        submitData.append("slug", slug);
        submitData.append("avatar", selectedFile);
      } else {
        // Usar JSON para envio sem arquivo
        submitData = {
          display_name: formData.display_name,
          bio: formData.bio,
          public_email: formData.public_email,
          website: formData.website ? `https://${formData.website}` : "",
          slug: slug
        };
      }

      if (profileData) {
        // Atualizar perfil existente
        response = await MarketplaceServices.updateSellerProfile(submitData);
      } else {
        // Criar novo perfil
        response = await MarketplaceServices.createSellerProfile(submitData);
      }

      // Nova estrutura da API: response.data contém os dados diretamente
      if (response.data && response.data.display_name) {
        const savedProfileData = response.data;
        
        // Atualizar o profileData mantendo dados adicionais se existirem
        setProfileData({
          ...profileData,
          ...savedProfileData,
          // Manter dados adicionais se já existirem
          user: profileData?.user,
          store: profileData?.store,
          stats: profileData?.stats,
        });
        
        // Limpar arquivo selecionado após sucesso
        setSelectedFile(null);
        setImageFile(null);
        
        // Atualizar estado da verificação do nome após salvar
        if (savedProfileData.display_name) {
          setNameAvailability({
            checked: true,
            available: true,
            error: null,
            suggestions: [],
            normalizedSlug: savedProfileData.slug || MarketplaceSlugServices.generateSlug(savedProfileData.display_name)
          });
        }
        
        // Usar Toast para notificação de sucesso
        Toast.fire({
          icon: "success",
          title: profileData ? "Perfil atualizado com sucesso!" : "Perfil criado com sucesso!",
        });

        // Usar setNotify para notificação adicional
        setNotify({
          title: "Sucesso!",
          message: `Seu perfil foi ${profileData ? 'atualizado' : 'criado'} com sucesso. As alterações já estão ativas.`,
          type: "success",
          visible: true,
        });
        
        // Recarregar dados do perfil após 2 segundos
        setTimeout(() => {
          loadProfile();
        }, 2000);
      }
    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      
      let errorMessage = "Erro ao salvar perfil. Tente novamente.";
      let errorTitle = "Erro!";
      
      if (error.response?.data) {
        // Verificar diferentes formatos de erro da API
        if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        }
        
        // Determinar se é erro ou warning baseado no status code
        if (error.response.status >= 400 && error.response.status < 500) {
          errorTitle = "Atenção!";
        } else if (error.response.status >= 500) {
          errorTitle = "Erro interno!";
          errorMessage = "Ocorreu um erro no servidor. Tente novamente em alguns instantes.";
        }
      }
      
      // Usar Swal.fire para erros críticos (modal blocking)
      await Swal.fire({
        icon: error.response?.status >= 500 ? "error" : "warning",
        title: errorTitle,
        text: errorMessage,
        confirmButtonColor: "var(--primary)",
      });
      
      // Usar setNotify para notificação adicional em caso de erro de validação
      if (error.response?.status >= 400 && error.response?.status < 500) {
        setNotify({
          title: "Dados inválidos",
          message: "Verifique os campos do formulário e tente novamente.",
          type: "warning",
          visible: true,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate("/marketplace");
  };

  if (loading) {
    return (
      <div className="sp-seller-profile-page">
        <div className="sp-loading-container">
          <MoonLoader size={40} color="var(--primary)" />
          <p className="sp-loading-text">Carregando perfil do vendedor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sp-seller-profile-page">
      {/* Hero Header Section */}
      <div className="sp-hero-section">
        <button onClick={handleBack} className="sp-back-button">
          <i className="las la-arrow-left" />
          <span>Voltar</span>
        </button>

        <div className="sp-hero-content">
          <div className="sp-hero-icon">
            <i className="las la-user-circle" />
          </div>
          <h1 className="sp-hero-title">Perfil do Vendedor</h1>
          <p className="sp-hero-subtitle">
            Configure seu perfil público no marketplace e aumente sua credibilidade
          </p>
        </div>

        {/* Statistics Preview */}
        {profileData?.stats && (
          <div className="sp-stats-preview">
            <div className="sp-stat-item">
              <span className="sp-stat-value">{profileData.stats.total_agents || 0}</span>
              <span className="sp-stat-label">Total de Agentes</span>
            </div>
            <div className="sp-stat-item">
              <span className="sp-stat-value">{profileData.stats.published_agents || 0}</span>
              <span className="sp-stat-label">Publicados</span>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="sp-main-content">
        <div className="sp-form-container">
          {error && (
            <div className="sp-error-banner">
              <i className="las la-exclamation-triangle" />
              <div>
                <h4>Erro ao carregar dados</h4>
                <p>{error}</p>
              </div>
              <button onClick={() => setError(null)} className="sp-error-close">
                <i className="las la-times" />
              </button>
            </div>
          )}

          <div className="sp-profile-card">
            {/* Profile Header with Image */}
            <div className="sp-profile-header">
              <div className="sp-image-section">
                <label className="sp-image-container">
                  {getProfileImage() ? (
                    <img
                      src={getProfileImage()}
                      alt="Avatar do vendedor"
                      className="sp-profile-image"
                    />
                  ) : (
                    <div className="sp-image-placeholder">
                      <i className="las la-user" />
                    </div>
                  )}

                  <div className="sp-camera-overlay">
                    <i className="las la-camera" />
                    <span>Alterar foto</span>
                  </div>

                  <input
                    accept="image/*"
                    type="file"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleImagePreview(file);
                      }
                    }}
                    className="sp-file-input"
                  />
                </label>

                <div className="sp-image-info">
                  <h3 className="sp-profile-name">
                    {profileData?.display_name || "Seu Perfil"}
                  </h3>
                  <p className="sp-profile-description">
                    Clique na imagem para atualizar sua foto
                  </p>
                </div>
              </div>
            </div>

            {/* Form Section */}
            <form onSubmit={handleSubmit(onSubmit)} className="sp-form">
              {/* Display Name Field */}
              <div className="sp-form-section">
                <div className="sp-section-header">
                  <div className="sp-section-icon">
                    <i className="las la-user" />
                  </div>
                  <h3>Identificação</h3>
                </div>

                <div className="sp-form-group">
                  <label className="sp-form-label">
                    <i className="las la-user-tag" />
                    Nome de Exibição
                    <span className="sp-required">*</span>
                  </label>
                  <div className="sp-input-with-action">
                    <input
                      type="text"
                      placeholder="Como você quer ser conhecido no marketplace"
                      className={`sp-form-input ${
                        errors.display_name ? "sp-invalid" : ""
                      } ${
                        nameAvailability.checked && nameAvailability.available === false ? "sp-invalid" : ""
                      } ${
                        nameAvailability.checked && nameAvailability.available === true ? "sp-valid" : ""
                      }`}
                      {...register("display_name", {
                        required: "Nome de exibição é obrigatório",
                        minLength: {
                          value: 2,
                          message: "Nome deve ter pelo menos 2 caracteres"
                        },
                        maxLength: {
                          value: 100,
                          message: "Nome deve ter no máximo 100 caracteres"
                        },
                        onChange: () => {
                          setNameAvailability({
                            checked: false,
                            available: null,
                            error: null,
                            suggestions: [],
                            normalizedSlug: null
                          });
                        }
                      })}
                    />
                    <button
                      type="button"
                      className="sp-verify-button"
                      disabled={checkingName || !watch("display_name") || watch("display_name").trim().length < 2}
                      onClick={handleCheckNameAvailability}
                    >
                      {checkingName ? (
                        <>
                          <MoonLoader size={16} color="currentColor" />
                          Verificando...
                        </>
                      ) : (
                        <>
                          <i className="las la-check" />
                          Verificar
                        </>
                      )}
                    </button>
                  </div>

                  {errors.display_name && (
                    <div className="sp-form-error">
                      <i className="las la-exclamation-circle" />
                      {errors.display_name.message}
                    </div>
                  )}

                  {nameAvailability.error && (
                    <div className="sp-form-error">
                      <i className="las la-exclamation-circle" />
                      {nameAvailability.error}
                    </div>
                  )}

                  {nameAvailability.checked && nameAvailability.available === true && (
                    <div className="sp-form-success">
                      <i className="las la-check-circle" />
                      Nome disponível! Seu perfil será acessível como: {nameAvailability.normalizedSlug}
                    </div>
                  )}

                  {nameAvailability.checked && nameAvailability.available === false && (
                    <>
                      <div className="sp-form-error">
                        <i className="las la-times-circle" />
                        Nome já está em uso
                      </div>
                      {nameAvailability.suggestions.length > 0 && (
                        <div className="sp-suggestions">
                          <p className="sp-suggestions-title">Sugestões disponíveis:</p>
                          <div className="sp-suggestions-list">
                            {nameAvailability.suggestions.slice(0, 3).map((suggestion, index) => (
                              <button
                                key={index}
                                type="button"
                                className="sp-suggestion-badge"
                                onClick={() => {
                                  setValue("display_name", suggestion);
                                  setNameAvailability({
                                    checked: false,
                                    available: null,
                                    error: null,
                                    suggestions: [],
                                    normalizedSlug: null
                                  });
                                }}
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  {!nameAvailability.checked && (
                    <div className="sp-form-hint">
                      <i className="las la-info-circle" />
                      O nome deve ser único. Clique em "Verificar" para confirmar a disponibilidade
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Information */}
              <div className="sp-form-section">
                <div className="sp-section-header">
                  <div className="sp-section-icon">
                    <i className="las la-address-book" />
                  </div>
                  <h3>Informações de Contato</h3>
                </div>

                <div className="sp-form-group">
                  <label className="sp-form-label">
                    <i className="las la-envelope" />
                    E-mail Público
                    <span className="sp-required">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="contato@exemplo.com"
                    className={`sp-form-input ${errors.public_email ? "sp-invalid" : ""}`}
                    {...register("public_email", {
                      required: "E-mail público é obrigatório",
                      pattern: {
                        value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: "Digite um e-mail válido"
                      }
                    })}
                  />
                  {errors.public_email && (
                    <div className="sp-form-error">
                      <i className="las la-exclamation-circle" />
                      {errors.public_email.message}
                    </div>
                  )}
                  <div className="sp-form-hint">
                    <i className="las la-info-circle" />
                    Este e-mail será exibido publicamente para contato
                  </div>
                </div>

                <div className="sp-form-group">
                  <label className="sp-form-label">
                    <i className="las la-globe" />
                    Website
                  </label>
                  <div className="sp-website-input">
                    <span className="sp-website-prefix">https://</span>
                    <input
                      type="text"
                      placeholder="www.meusite.com"
                      className={`sp-form-input sp-website-field ${errors.website ? "sp-invalid" : ""}`}
                      {...register("website", {
                        pattern: {
                          value: /^[a-zA-Z0-9][a-zA-Z0-9-._]*[a-zA-Z0-9]\.[a-zA-Z]{2,}$/,
                          message: "Digite um domínio válido (ex: www.exemplo.com)"
                        }
                      })}
                    />
                  </div>
                  {errors.website && (
                    <div className="sp-form-error">
                      <i className="las la-exclamation-circle" />
                      {errors.website.message}
                    </div>
                  )}
                  <div className="sp-form-hint">
                    <i className="las la-info-circle" />
                    Link para seu site ou portfólio (opcional)
                  </div>
                </div>
              </div>

              {/* Biography */}
              <div className="sp-form-section">
                <div className="sp-section-header">
                  <div className="sp-section-icon">
                    <i className="las la-user-edit" />
                  </div>
                  <h3>Sobre Você</h3>
                </div>

                <div className="sp-form-group">
                  <label className="sp-form-label">
                    <i className="las la-file-alt" />
                    Biografia
                    <span className="sp-required">*</span>
                  </label>
                  <textarea
                    rows={5}
                    placeholder="Conte um pouco sobre você, sua experiência e especialidades..."
                    className={`sp-form-textarea ${errors.bio ? "sp-invalid" : ""}`}
                    {...register("bio", {
                      required: "Biografia é obrigatória",
                      minLength: {
                        value: 50,
                        message: "Biografia deve ter pelo menos 50 caracteres"
                      },
                      maxLength: {
                        value: 1000,
                        message: "Biografia deve ter no máximo 1000 caracteres"
                      }
                    })}
                  />
                  {errors.bio && (
                    <div className="sp-form-error">
                      <i className="las la-exclamation-circle" />
                      {errors.bio.message}
                    </div>
                  )}
                  <div className="sp-form-hint">
                    <i className="las la-info-circle" />
                    Descreva sua experiência, especialidades e diferenciais
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="sp-form-actions">
                <button
                  type="submit"
                  className="sp-submit-button"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <MoonLoader size={18} color="currentColor" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <>
                      <i className="las la-save" />
                      <span>Salvar Perfil</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar with Tips */}
        <div className="sp-sidebar">
          <div className="sp-tips-card">
            <div className="sp-tips-header">
              <i className="las la-lightbulb" />
              <h4>Dicas para seu Perfil</h4>
            </div>
            <div className="sp-tips-content">
              <div className="sp-tip-item">
                <span className="sp-tip-emoji">📸</span>
                <div>
                  <strong>Use uma foto profissional</strong>
                  <p>Uma boa foto aumenta a confiança dos clientes</p>
                </div>
              </div>
              <div className="sp-tip-item">
                <span className="sp-tip-emoji">✍️</span>
                <div>
                  <strong>Biografia detalhada</strong>
                  <p>Conte sua experiência e especialidades</p>
                </div>
              </div>
              <div className="sp-tip-item">
                <span className="sp-tip-emoji">🔗</span>
                <div>
                  <strong>Adicione seu website</strong>
                  <p>Mostre seu portfólio ou site pessoal</p>
                </div>
              </div>
              <div className="sp-tip-item">
                <span className="sp-tip-emoji">📧</span>
                <div>
                  <strong>E-mail de contato</strong>
                  <p>Use um e-mail profissional para contato</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SellerProfile;